import type { CameraView, PropDef, Vec2 } from '../core/types';
import type { Track } from '../game/track';
import { hash01, TAU } from '../core/math';

export interface TrackLayer {
  canvas: HTMLCanvasElement;
  /** világkoordináta, ahova a canvas bal felső sarka esik */
  minX: number;
  minY: number;
  width: number;
  height: number;
  scale: number;
}

const BUILDING_COLORS = ['#6f6152', '#7d6a58', '#5d6570', '#73655a', '#88796a', '#646b74'];
const WINDOW_LIT = '#ffd98a';
const WINDOW_DARK = 'rgba(30,36,46,0.55)';
const TREE_GREEN = ['#4f7a4a', '#5f8f55', '#456e43'];
/** a belső ív parkos szakaszai (s 0..1) */
const PARK_ZONES: [number, number][] = [
  [0.1, 0.32],
  [0.55, 0.78],
];

const CITY_ROOFS = ['#343b48', '#3a4150', '#2f3644', '#3d4452'];

/** A pályán kívüli terület: várostömbök tetői, hogy ne legyen üres a külváros. */
const drawCityBlocks = (ctx: CanvasRenderingContext2D, minX: number, minY: number, w: number, h: number): void => {
  const bw = 188;
  const bh = 152;
  const cols = Math.ceil(w / bw) + 1;
  const rows = Math.ceil(h / bh) + 1;
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const h1 = hash01(i * 7919 + j * 104729);
      if (h1 < 0.14) continue;
      const x = minX + i * bw + 7 + h1 * 9;
      const y = minY + j * bh + 7 + hash01(i * 3 + j * 31) * 9;
      const ww = bw - 26 - hash01(i * 13 + j) * 18;
      const hh = bh - 24 - hash01(i * 17 + j * 7) * 16;
      ctx.fillStyle = CITY_ROOFS[(i * 3 + j * 5) % CITY_ROOFS.length];
      ctx.fillRect(x, y, ww, hh);
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, ww, hh);
      const lit = hash01(i * 31 + j * 17) > 0.55;
      ctx.fillStyle = lit ? 'rgba(255,217,138,0.16)' : 'rgba(0,0,0,0.12)';
      for (let k = 0; k < 3; k++) ctx.fillRect(x + 10 + k * 24, y + hh * 0.52, 8, 8);
    }
  }
};

const tracePath = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const s = track.samples;
  ctx.beginPath();
  ctx.moveTo(s[0].pos.x, s[0].pos.y);
  for (let i = 1; i < s.length; i++) ctx.lineTo(s[i].pos.x, s[i].pos.y);
  ctx.closePath();
};

const polyline = (ctx: CanvasRenderingContext2D, points: Vec2[]): void => {
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
};

/** oldalirányban eltolt, sima vonal a pálya mentén */
const offsetLine = (track: Track, lateral: number, from = 0, to = 1): Vec2[] => {
  const pts: Vec2[] = [];
  const n = 240;
  for (let i = 0; i <= n; i++) pts.push(track.posAt(from + ((to - from) * i) / n, lateral));
  return pts;
};

const drawBuildings = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  const angle = Math.atan2(tangent.y, tangent.x);
  const scale = prop.scale ?? 1;
  const seed = Math.round(prop.s * 10000) + (prop.lateral > 0 ? 91 : 7);
  const w = (56 + hash01(seed) * 44) * scale;
  const d = (58 + hash01(seed + 3) * 48) * scale;
  const color = BUILDING_COLORS[(prop.variant ?? 0) % BUILDING_COLORS.length];

  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(angle);

  // talp-árnyék
  ctx.fillStyle = 'rgba(10,12,18,0.32)';
  ctx.fillRect(-w / 2 + 9, -d / 2 + 13, w, d);

  // tető
  ctx.fillStyle = '#2f3644';
  ctx.fillRect(-w / 2, -d / 2, w, d);

  // homlokfal
  ctx.fillStyle = color;
  ctx.fillRect(-w / 2 + 4, -d / 2 + 4, w - 8, d - 8);
  ctx.strokeStyle = 'rgba(16,19,28,0.55)';
  ctx.lineWidth = 2;
  ctx.strokeRect(-w / 2 + 4, -d / 2 + 4, w - 8, d - 8);

  // ablakrács
  const cols = Math.max(2, Math.floor((w - 20) / 17));
  const rows = Math.max(2, Math.floor((d - 20) / 17));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const lit = hash01(seed + r * 31 + c * 7) > 0.62;
      ctx.fillStyle = lit ? WINDOW_LIT : WINDOW_DARK;
      ctx.fillRect(-w / 2 + 12 + c * 17, -d / 2 + 12 + r * 17, 9, 9);
    }
  }

  // tető-perem
  ctx.strokeStyle = 'rgba(255,236,200,0.22)';
  ctx.lineWidth = 2;
  ctx.strokeRect(-w / 2 + 2, -d / 2 + 2, w - 4, d - 4);
  ctx.restore();
};

const drawTree = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const scale = prop.scale ?? 1;
  const seed = Math.round(prop.s * 9000) + 13;
  const r = (16 + hash01(seed) * 9) * scale;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = 'rgba(10,12,18,0.3)';
  ctx.beginPath();
  ctx.ellipse(5, 7, r * 0.9, r * 0.6, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#4a3a2c';
  ctx.fillRect(-3, -3, 6, 10);
  const green = TREE_GREEN[(prop.variant ?? 0) % TREE_GREEN.length];
  ctx.fillStyle = green;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.13)';
  ctx.beginPath();
  ctx.arc(-r * 0.28, -r * 0.3, r * 0.55, 0, TAU);
  ctx.fill();
  ctx.restore();
};

const drawLamp = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  const inward = Math.sign(prop.lateral) * -1;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.strokeStyle = '#2c313b';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, inward * 26);
  ctx.stroke();
  ctx.fillStyle = '#ffd98a';
  ctx.beginPath();
  ctx.arc(0, inward * 26, 6, 0, TAU);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,217,138,0.18)';
  ctx.beginPath();
  ctx.arc(0, inward * 26, 16, 0, TAU);
  ctx.fill();
  ctx.restore();
};

const drawKiosk = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.fillStyle = 'rgba(10,12,18,0.3)';
  ctx.fillRect(-20, -14, 44, 30);
  ctx.fillStyle = '#e2d3b4';
  ctx.fillRect(-24, -18, 48, 34);
  ctx.fillStyle = '#c9543f';
  for (let i = 0; i < 4; i++) ctx.fillRect(-22 + i * 12, -16, 6, 30);
  ctx.fillStyle = '#3b4250';
  ctx.fillRect(-24, 8, 48, 6);
  ctx.restore();
};

const drawBench = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.fillStyle = '#6b4f36';
  ctx.fillRect(-16, -7, 32, 6);
  ctx.fillStyle = '#4a3a2c';
  ctx.fillRect(-15, 3, 3, 5);
  ctx.fillRect(12, 3, 3, 5);
  ctx.restore();
};

const drawCone = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = '#e8792f';
  ctx.beginPath();
  ctx.moveTo(0, -11);
  ctx.lineTo(9, 9);
  ctx.lineTo(-9, 9);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#f4f1e8';
  ctx.fillRect(-6, 0, 12, 4);
  ctx.restore();
};

const drawParkedCar = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  const palette = ['#8c5f5f', '#5f6f8c', '#6f8c6a', '#8c8360'];
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.fillStyle = 'rgba(10,12,18,0.28)';
  ctx.fillRect(-23, -11, 50, 26);
  ctx.fillStyle = palette[(prop.variant ?? 0) % palette.length];
  ctx.fillRect(-26, -13, 52, 26);
  ctx.fillStyle = '#a8d4e2';
  ctx.fillRect(-8, -10, 16, 20);
  ctx.fillStyle = '#1a1c22';
  ctx.fillRect(-20, -15, 12, 4);
  ctx.fillRect(-20, 11, 12, 4);
  ctx.fillRect(10, -15, 12, 4);
  ctx.fillRect(10, 11, 12, 4);
  ctx.restore();
};

const drawSign = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = '#2c313b';
  ctx.fillRect(-2, -20, 4, 22);
  ctx.fillStyle = prop.variant === 1 ? '#3ec6c0' : prop.variant === 2 ? '#ffc857' : '#f4f1e8';
  ctx.beginPath();
  ctx.arc(0, -24, 9, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = '#2c313b';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
};

const drawSmallProp = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  if (prop.kind === 'hydrant') {
    ctx.fillStyle = '#c9543f';
    ctx.fillRect(-5, -10, 10, 16);
    ctx.fillStyle = '#e8792f';
    ctx.fillRect(-8, -4, 16, 5);
  } else if (prop.kind === 'bin') {
    ctx.fillStyle = '#4a5563';
    ctx.fillRect(-7, -9, 14, 18);
    ctx.fillStyle = '#5f6b7a';
    ctx.fillRect(-8, -12, 16, 4);
  }
  ctx.restore();
};

const drawCrosswalk = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, 0);
  const tangent = track.tangentAt(prop.s);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.fillStyle = 'rgba(244,241,232,0.86)';
  const half = track.def.roadHalfWidth - 8;
  for (let lat = -half; lat < half; lat += 24) {
    ctx.fillRect(-14, lat, 28, 13);
  }
  ctx.restore();
};

const drawArrow = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  // festett nyilak: halványak, hogy ne tűnjenek interaktívnak
  ctx.strokeStyle = 'rgba(244,241,232,0.3)';
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-22, -12);
  ctx.lineTo(6, 0);
  ctx.lineTo(-22, 12);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(2, 0);
  ctx.lineTo(22, 0);
  ctx.stroke();
  ctx.restore();
};

const drawTram = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const L = track.totalLength;
  const from = 0.455;
  const to = 0.545;
  ctx.save();
  for (const lateral of [-19, 19]) {
    const pts: Vec2[] = [];
    const steps = 90;
    for (let i = 0; i <= steps; i++) pts.push(track.posAt(from + ((to - from) * i) / steps, lateral));
    polyline(ctx, pts);
    ctx.strokeStyle = 'rgba(28,30,36,0.55)';
    ctx.lineWidth = 6;
    ctx.stroke();
    polyline(ctx, pts);
    ctx.strokeStyle = 'rgba(190,205,220,0.55)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }
  // talpfák
  for (let s = from * L; s < to * L; s += 22) {
    const p = track.posAt(s / L, 0);
    const t = track.tangentAt(s / L);
    const a = Math.atan2(t.y, t.x);
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(a);
    ctx.fillStyle = 'rgba(40,38,34,0.32)';
    ctx.fillRect(-3, -28, 6, 56);
    ctx.restore();
  }
  ctx.restore();
};

const drawStartLine = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const progress = track.startS / track.totalLength;
  const pos = track.posAt(progress, 0);
  const tangent = track.tangentAt(progress);
  const half = track.def.roadHalfWidth;
  const cols = 12;
  const cell = (half * 2) / cols;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  for (let row = 0; row < 2; row++) {
    for (let c = 0; c < cols; c++) {
      ctx.fillStyle = (row + c) % 2 === 0 ? '#f4f1e8' : '#20242c';
      ctx.fillRect(-20 + row * 20, -half + c * cell, 20, cell);
    }
  }
  ctx.font = '700 26px "Trebuchet MS", sans-serif';
  ctx.fillStyle = 'rgba(244,241,232,0.75)';
  ctx.textAlign = 'center';
  ctx.save();
  ctx.translate(60, 0);
  ctx.rotate(Math.PI / 2);
  ctx.fillText('START', 0, 8);
  ctx.restore();
  ctx.restore();
};

const drawWorld = (ctx: CanvasRenderingContext2D, track: Track, minX: number, minY: number, w: number, h: number): void => {
  const a = track.def.ambient;
  const roadHalf = track.def.roadHalfWidth;
  const kerb = track.def.kerbWidth;
  const corridor = roadHalf + kerb + track.def.sidewalkWidth;

  ctx.fillStyle = a.wall;
  ctx.fillRect(minX, minY, w, h);
  drawCityBlocks(ctx, minX, minY, w, h);

  // belső terület: park
  const infield = offsetLine(track, -(corridor + 70), 0, 1);
  ctx.beginPath();
  ctx.moveTo(infield[0].x, infield[0].y);
  for (let i = 1; i < infield.length; i++) ctx.lineTo(infield[i].x, infield[i].y);
  ctx.closePath();
  ctx.fillStyle = '#3c5a3f';
  ctx.fill();

  // várostömb-előtér: a járda és a házak közti sáv
  tracePath(ctx, track);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.strokeStyle = '#6f6c66';
  ctx.lineWidth = (corridor + 78) * 2;
  ctx.stroke();

  // járda-sáv
  tracePath(ctx, track);
  ctx.strokeStyle = a.sidewalk;
  ctx.lineWidth = corridor * 2;
  ctx.stroke();

  // park-zöld a belső íven
  for (const [from, to] of PARK_ZONES) {
    polyline(ctx, offsetLine(track, -192, from, to));
    ctx.strokeStyle = '#3f5c42';
    ctx.lineWidth = 132;
    ctx.stroke();
    polyline(ctx, offsetLine(track, -132, from, to));
    ctx.strokeStyle = 'rgba(150,180,130,0.3)';
    ctx.lineWidth = 10;
    ctx.stroke();
  }

  // szegélykő
  tracePath(ctx, track);
  ctx.strokeStyle = a.kerb;
  ctx.lineWidth = (roadHalf + kerb) * 2;
  ctx.stroke();

  // aszfalt
  tracePath(ctx, track);
  ctx.strokeStyle = a.asphalt;
  ctx.lineWidth = roadHalf * 2;
  ctx.stroke();

  // aszfalt-szemcse
  for (let i = 0; i < 2200; i++) {
    const s = hash01(i * 7 + 1);
    const lat = (hash01(i * 13 + 5) * 2 - 1) * (roadHalf - 6);
    const p = track.posAt(s, lat);
    const size = 1 + hash01(i * 17 + 3) * 2.5;
    ctx.fillStyle = hash01(i) > 0.5 ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.05)';
    ctx.fillRect(p.x, p.y, size, size);
  }

  // sávfestés
  ctx.save();
  ctx.setLineDash([30, 26]);
  ctx.lineWidth = 3;
  ctx.strokeStyle = a.lane;
  for (const lateral of [-48, 0, 48]) {
    polyline(ctx, offsetLine(track, lateral));
    ctx.stroke();
  }
  ctx.restore();

  // külső folytonos vonal
  ctx.save();
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(244,241,232,0.4)';
  for (const lateral of [-roadHalf + 6, roadHalf - 6]) {
    polyline(ctx, offsetLine(track, lateral));
    ctx.stroke();
  }
  ctx.restore();

  // szegélykő csíkozás
  ctx.save();
  ctx.setLineDash([26, 26]);
  ctx.lineWidth = kerb * 0.8;
  ctx.strokeStyle = 'rgba(244,241,232,0.92)';
  for (const lateral of [-roadHalf - kerb / 2, roadHalf + kerb / 2]) {
    polyline(ctx, offsetLine(track, lateral));
    ctx.stroke();
  }
  ctx.restore();

  drawTram(ctx, track);  drawStartLine(ctx, track);

  // propok: y szerint rendezve, hogy a közelebbiek kerüljenek felülre
  const sorted = [...track.def.props].sort((p, q) => track.posAt(p.s, p.lateral).y - track.posAt(q.s, q.lateral).y);
  for (const prop of sorted) {
    switch (prop.kind) {
      case 'building':
        drawBuildings(ctx, track, prop);
        break;
      case 'tree':
        drawTree(ctx, track, prop);
        break;
      case 'lamp':
        drawLamp(ctx, track, prop);
        break;
      case 'kiosk':
        drawKiosk(ctx, track, prop);
        break;
      case 'bench':
        drawBench(ctx, track, prop);
        break;
      case 'cone':
        drawCone(ctx, track, prop);
        break;
      case 'parkedCar':
        drawParkedCar(ctx, track, prop);
        break;
      case 'sign':
        drawSign(ctx, track, prop);
        break;
      case 'hydrant':
      case 'bin':
        drawSmallProp(ctx, track, prop);
        break;
      case 'crosswalk':
        drawCrosswalk(ctx, track, prop);
        break;
      case 'arrow':
        drawArrow(ctx, track, prop);
        break;
      case 'puddle': {
        const p = track.posAt(prop.s, prop.lateral);
        ctx.save();
        ctx.fillStyle = 'rgba(96,124,146,0.5)';
        ctx.beginPath();
        ctx.ellipse(p.x, p.y, 34, 18, 0, 0, TAU);
        ctx.fill();
        ctx.fillStyle = 'rgba(200,225,240,0.25)';
        ctx.beginPath();
        ctx.ellipse(p.x - 8, p.y - 4, 14, 6, 0, 0, TAU);
        ctx.fill();
        ctx.restore();
        break;
      }
      default:
        break;
    }
  }
};

export function buildTrackLayer(track: Track): TrackLayer {
  const pad = 620;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const s of track.samples) {
    minX = Math.min(minX, s.pos.x);
    minY = Math.min(minY, s.pos.y);
    maxX = Math.max(maxX, s.pos.x);
    maxY = Math.max(maxY, s.pos.y);
  }
  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;

  const worldW = maxX - minX;
  const worldH = maxY - minY;
  const maxDim = 4096;
  const scale = Math.min(1, maxDim / Math.max(worldW, worldH));

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(worldW * scale));
  canvas.height = Math.max(1, Math.ceil(worldH * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Nem sikerült a pálya-réteg canvasát létrehozni.');

  ctx.setTransform(scale, 0, 0, scale, -minX * scale, -minY * scale);
  drawWorld(ctx, track, minX, minY, worldW, worldH);

  return {
    canvas,
    minX,
    minY,
    width: canvas.width / scale,
    height: canvas.height / scale,
    scale,
  };
}

export function drawTrackLayer(ctx: CanvasRenderingContext2D, layer: TrackLayer, _cam: CameraView): void {
  ctx.drawImage(layer.canvas, layer.minX, layer.minY, layer.width, layer.height);
}

export function drawTrackDynamic(ctx: CanvasRenderingContext2D, track: Track, cam: CameraView, time: number): void {
  for (const pad of track.def.boostPads) {
    const startS = pad.s * track.totalLength;
    const center = track.posAt(pad.s, pad.lateral);
    if (
      center.x < cam.bounds.minX - 200 ||
      center.x > cam.bounds.maxX + 200 ||
      center.y < cam.bounds.minY - 200 ||
      center.y > cam.bounds.maxY + 200
    ) {
      continue;
    }
    // a pad alapsávja: ettől látszik, hogy ez egy felület, nem festék
    const band: Vec2[] = [];
    const edgeL: Vec2[] = [];
    const edgeR: Vec2[] = [];
    for (let d = -6; d <= pad.length + 6; d += 18) {
      const prog = track.progressOfS(startS + d);
      band.push(track.posAt(prog, pad.lateral));
      edgeL.push(track.posAt(prog, pad.lateral - pad.width / 2 + 5));
      edgeR.push(track.posAt(prog, pad.lateral + pad.width / 2 - 5));
    }
    ctx.save();
    polyline(ctx, band);
    ctx.lineCap = 'butt';
    ctx.strokeStyle = 'rgba(255,132,40,0.26)';
    ctx.lineWidth = pad.width;
    ctx.stroke();
    ctx.lineCap = 'round';
    ctx.lineWidth = 3;
    for (const edge of [edgeL, edgeR]) {
      polyline(ctx, edge);
      ctx.strokeStyle = 'rgba(255,200,87,0.75)';
      ctx.stroke();
    }
    polyline(ctx, band);
    ctx.strokeStyle = 'rgba(255,220,140,0.5)';
    ctx.lineWidth = 3;
    ctx.setLineDash([12, 14]);
    ctx.stroke();
    ctx.restore();

    const count = Math.floor(pad.length / 42);
    for (let i = 0; i < count; i++) {
      const offset = (i * 42 + ((time * 90) % 42)) % pad.length;
      const progress = track.progressOfS(startS + offset);
      const p = track.posAt(progress, pad.lateral);
      const t = track.tangentAt(progress);
      const fade = 1 - offset / pad.length;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(Math.atan2(t.y, t.x));
      ctx.strokeStyle = `rgba(255,200,87,${(0.25 + fade * 0.6).toFixed(3)})`;
      ctx.lineWidth = 9;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(-14, -26);
      ctx.lineTo(6, 0);
      ctx.lineTo(-14, 26);
      ctx.stroke();
      ctx.restore();
    }
  }
}

interface MinimapLayout {
  scale: number;
  offsetX: number;
  offsetY: number;
}

const minimapLayout = (track: Track, size: number): MinimapLayout => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const s of track.samples) {
    minX = Math.min(minX, s.pos.x);
    minY = Math.min(minY, s.pos.y);
    maxX = Math.max(maxX, s.pos.x);
    maxY = Math.max(maxY, s.pos.y);
  }
  const pad = 18;
  const scale = Math.min((size - pad * 2) / (maxX - minX), (size - pad * 2) / (maxY - minY));
  return {
    scale,
    offsetX: (size - (maxX - minX) * scale) / 2 - minX * scale,
    offsetY: (size - (maxY - minY) * scale) / 2 - minY * scale,
  };
};

/** A pálya adott pontja a minimap canvasán, 0..1 arányban. */
export function minimapPoint(track: Track, progress: number, size: number): { x: number; y: number } {
  const layout = minimapLayout(track, size);
  const p = track.posAt(progress, 0);
  return {
    x: (layout.offsetX + p.x * layout.scale) / size,
    y: (layout.offsetY + p.y * layout.scale) / size,
  };
}

export function buildMinimap(track: Track, size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const layout = minimapLayout(track, size);
  ctx.translate(layout.offsetX, layout.offsetY);
  ctx.scale(layout.scale, layout.scale);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  tracePath(ctx, track);
  ctx.strokeStyle = 'rgba(10,12,18,0.9)';
  ctx.lineWidth = 100;
  ctx.stroke();

  tracePath(ctx, track);
  ctx.strokeStyle = '#f4f1e8';
  ctx.lineWidth = 62;
  ctx.stroke();

  tracePath(ctx, track);
  ctx.strokeStyle = 'rgba(255,200,87,0.55)';
  ctx.lineWidth = 16;
  ctx.setLineDash([90, 90]);
  ctx.stroke();
  ctx.setLineDash([]);

  const start = track.posAt(track.startS / track.totalLength, 0);
  ctx.fillStyle = '#ff5c4d';
  ctx.beginPath();
  ctx.arc(start.x, start.y, 58, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = '#f4f1e8';
  ctx.lineWidth = 20;
  ctx.stroke();

  return canvas;
}
